import React, { useState, useEffect } from 'react';
import { Save, Shield, Bell, Settings as SettingsIcon, Database, Loader } from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import { lerToken } from '../../services/sessao';

const INITIAL_SETTINGS = {
  nomeEmpresa: 'Alencastro Consultoria',
  emailContato: 'suporte@gestaocreche.com.br',
  telefoneSuporte: '+55 48 99111-8686',
  websitePrincipal: 'https://www.gestaocreche.com.br',

  notificacoesEmail: true,
  notificacoesSlack: false,
  notificacoesWhatsapp: false,

  autenticacaoDoisFatores: true,
  exigirLGPD: true,
  backupDiario: true,

  tempoSessao: '24',
  tentativasLogin: '5',
  integracao3d: 'Ativada',
};

type Settings = typeof INITIAL_SETTINGS;

function getToken() {
  return lerToken('saas') || '';
}

export default function ConfiguracoesPage() {
  const { success, error } = useNotification();
  const [settings, setSettings] = useState<Settings>(INITIAL_SETTINGS);
  // Baseline = última versão persistida no servidor (referência para "houve alteração?")
  const [baseline, setBaseline] = useState<Settings>(INITIAL_SETTINGS);
  const [isModified, setIsModified] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Carregar configurações persistidas do backend
  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  async function carregarConfiguracoes() {
    setCarregando(true);
    try {
      const resp = await fetch('/api/saas/configuracoes', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      const carregado = { ...INITIAL_SETTINGS, ...data } as Settings;
      setSettings(carregado);
      setBaseline(carregado);
    } catch (err: any) {
      error('Não foi possível carregar as configurações: ' + (err.message || err));
    } finally {
      setCarregando(false);
    }
  }

  // Verificar se houve alterações em relação ao que está persistido
  useEffect(() => {
    setIsModified(JSON.stringify(settings) !== JSON.stringify(baseline));
  }, [settings, baseline]);

  const handleSave = async () => {
    setSalvando(true);
    try {
      const resp = await fetch('/api/saas/configuracoes', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(settings),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.message || `Erro ${resp.status}`);
      }
      const salvo = await resp.json();
      const atualizado = { ...INITIAL_SETTINGS, ...salvo } as Settings;
      setSettings(atualizado);
      setBaseline(atualizado);
      success('Configurações salvas com sucesso!');
    } catch (err: any) {
      error('Erro ao salvar configurações: ' + (err.message || err));
    } finally {
      setSalvando(false);
    }
  };

  const handleCancel = () => {
    setSettings(baseline);
  };

  const handleChange = (field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white shadow-lg">
        <h1 className="text-4xl font-bold mb-2">Configurações</h1>
        <p className="text-blue-100">Gerencie as configurações gerais do SaaS</p>
      </div>

      {/* Abas de Configuração */}
      <div className="space-y-6">
        {/* Seção 1: Informações Gerais */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <SettingsIcon className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Informações Gerais</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Empresa</label>
              <input
                type="text"
                value={settings.nomeEmpresa}
                onChange={(e) => handleChange('nomeEmpresa', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email de Contato</label>
              <input
                type="email"
                value={settings.emailContato}
                onChange={(e) => handleChange('emailContato', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefone de Suporte</label>
              <input
                type="tel"
                value={settings.telefoneSuporte}
                onChange={(e) => handleChange('telefoneSuporte', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website Principal</label>
              <input
                type="url"
                value={settings.websitePrincipal}
                onChange={(e) => handleChange('websitePrincipal', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Seção 2: Notificações */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="text-orange-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Preferências de Notificação</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Email</p>
                <p className="text-sm text-gray-600">Receba notificações por email</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notificacoesEmail}
                onChange={(e) => handleChange('notificacoesEmail', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Slack</p>
                <p className="text-sm text-gray-600">Receba notificações no Slack</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notificacoesSlack}
                onChange={(e) => handleChange('notificacoesSlack', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">WhatsApp</p>
                <p className="text-sm text-gray-600">Receba notificações via WhatsApp</p>
              </div>
              <input
                type="checkbox"
                checked={settings.notificacoesWhatsapp}
                onChange={(e) => handleChange('notificacoesWhatsapp', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Seção 3: Segurança */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-green-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Segurança</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Autenticação de Dois Fatores</p>
                <p className="text-sm text-gray-600">Exigir 2FA para admins SaaS</p>
              </div>
              <input
                type="checkbox"
                checked={settings.autenticacaoDoisFatores}
                onChange={(e) => handleChange('autenticacaoDoisFatores', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Aceitar LGPD Obrigatório</p>
                <p className="text-sm text-gray-600">Exigir aceite de LGPD antes de usar</p>
              </div>
              <input
                type="checkbox"
                checked={settings.exigirLGPD}
                onChange={(e) => handleChange('exigirLGPD', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Backup Diário</p>
                <p className="text-sm text-gray-600">Realizar backups automáticos diários</p>
              </div>
              <input
                type="checkbox"
                checked={settings.backupDiario}
                onChange={(e) => handleChange('backupDiario', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <p className="font-medium text-gray-900">Tempo de Sessão</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={settings.tempoSessao}
                  onChange={(e) => handleChange('tempoSessao', e.target.value)}
                  className="w-20 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-600">horas</span>
              </div>
            </div>

            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <p className="font-medium text-gray-900">Tentativas de Login Máximas</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={settings.tentativasLogin}
                  onChange={(e) => handleChange('tentativasLogin', e.target.value)}
                  className="w-20 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-600">tentativas antes de bloquear</span>
              </div>
            </div>
          </div>
        </div>

        {/* Seção 4: Integrações */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <Database className="text-purple-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Integrações</h2>
          </div>

          <div className="space-y-4">
            <div className="p-4 border border-gray-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Integração 3D Secure</p>
                <p className="text-sm text-gray-600">Sistema de validação de pagamentos</p>
              </div>
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold text-sm">
                {settings.integracao3d}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Botão Salvar */}
      <div className="flex justify-end gap-4">
        <button
          onClick={handleCancel}
          disabled={!isModified || salvando}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            isModified && !salvando
              ? 'bg-gray-200 text-gray-900 hover:bg-gray-300 cursor-pointer'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={!isModified || salvando || carregando}
          className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
            isModified && !salvando && !carregando
              ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer shadow-lg hover:shadow-xl'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {salvando ? <Loader size={20} className="animate-spin" /> : <Save size={20} />}
          {salvando ? 'Salvando...' : 'Salvar Configurações'}
        </button>
      </div>
    </div>
  );
}
